import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
// eslint-disable-next-line max-len
import { PublisherTrafficSourceApiService } from '../../features/supply/publishers/publisher-configurations/_services/publisher-traffic-source.api-service';
import {
  featureToggleKeys,
  OptionsLists,
  PublisherEdit,
  PublisherTrafficSource,
  ResponseFromServer,
  SharedStoreAssetConfig,
  SharedStoreAssetKey
} from '../_models/models';
import { Observable, of } from 'rxjs';
import { filter, finalize, first, shareReplay, switchMap } from 'rxjs/operators';
import { sharedAssetsConfigs, SharedStoreResponseType } from './shared-store.config';

@Injectable({ providedIn: 'root' })

export class SharedStoreService {

  sharedAssetsConfigs = sharedAssetsConfigs;

  // The single source of truth for how each shared asset is fetched. Each entry is a typed call
  // against whichever service owns the endpoint, so moving/renaming a method is a compile error
  // rather than a runtime failure.
  private readonly assetRequests: Record<SharedStoreAssetKey, (...args: any[]) => Observable<any>>;

  constructor(
    private api: ApiService,
    private publisherTrafficSourceApi: PublisherTrafficSourceApiService,
  ) {

    /*
    - No stringly-typed dispatch — this.api[methodName] is eliminated for shared-store. 
    The original bug (method moved off ApiService) is now a compile error: 
    point a map entry at a service that lacks the method and the build fails.
    - Multi-service native — trafficSourcesByPublisherId calls PublisherTrafficSourceApiService, 
    everything else calls ApiService, and future extractions just edit one line.
    */
    this.assetRequests = {
      featureToggles: () => this.api.getFeatureToggles([...featureToggleKeys]),
      optionsLists: () => this.api.getOptionsLists(),
      combinedSelectOptions: () => this.api.getCombinedSelectOptions(),
      countries: () => this.api.getCountries(),
      opsOwners: () => this.api.getOpsOwners(),
      bizdevOwners: () => this.api.getBizdevOwners(),
      timezones: () => this.api.getTimezonesList(),
      currencies: () => this.api.getCurrenciesList(),
      trafficSources: () => this.api.getJavaTrafficSources(),
      trafficSourcesByPublisherId: (publisherId: number) => this.publisherTrafficSourceApi.getPublisherTrafficSources(publisherId),
      ssps: () => this.api.getSspBasic(),
      iabCategories: () => this.api.getIabCategories(),
      dspAccountsGrouped: () => this.api.getSupplyDspAccountsGrouped(),
      publisherForLayout: (publisherId: number) => this.api.getPublisherById(publisherId),
    };
  }

  getGlobalAppAssets(currentUser): void {
    this.getSharedAsset('featureToggles').subscribe();
    this.getSharedAsset('optionsLists').subscribe();
    this.getSharedAsset('timezones').subscribe();
    this.getSharedAsset('currencies').subscribe();
    this.getSharedAsset('dspAccountsGrouped').subscribe();
    this.getSharedAsset('countries').subscribe();
    if (currentUser.userType === 'INTERNAL') {
      this.getSharedAsset('bizdevOwners').subscribe();
      this.getSharedAsset('opsOwners').subscribe();
    } else if (currentUser.publisherId) {
      this.getPublisherForLayout(currentUser.publisherId).subscribe();
      this.getTrafficSourcesByPublisherId(currentUser.publisherId).subscribe();
    }
  }

  getSharedAsset(assetKey: SharedStoreAssetKey, ...requestArgs: any[]): Observable<ResponseFromServer<any> | any> {
    const config: SharedStoreAssetConfig<SharedStoreResponseType> = this.sharedAssetsConfigs[assetKey];
    // If we already have the response (the stored asset), we can just re-share it.
    if (config.response$) {
      return config.response$;
    }
    // If there's no stored asset and the asset is currently not loading, we need to call the asset's request.
    if (!config.isLoading) {
      config.isLoading = true;
      // Resetting the queue. In case any other source is trying to get this asset while it's loading from the DB,
      // we put it in a queue-subject, and once the asset has resolved correctly, all items in the queue
      // will get that same response.
      config.queueSubject.next(null);
      return this.assetRequests[assetKey](...requestArgs).pipe(
        switchMap((res: ResponseFromServer<any>) => {
          // We need to make sure the response is correct, the config contains a small test function to validate that.
          // A failing test means a malformed response: pass it through uncached so the subscriber still gets a
          // stream (never undefined) and a later call can retry.
          if (!config.responseIntegrityTest(res)) {
            return of(res);
          }
          // Handle any response modifications, if needed.
          if (config.responseHandler) {
            res = config.responseHandler(res, config);
          }
          // Once the test passed we wrap the response in an observable with the shareReplay operator (the caching pipe)
          // this is used in the future whenever any component/service needs this asset again (see the first "if" of this method).
          config.response$ = of(res).pipe(shareReplay(1));
          // Notifying any members in the queue that the response has arrived (with the actual response).
          config.queueSubject.next(config.response$);
          // Returning the response to original subscriber who requested this asset first.
          return config.response$;
        }),
        finalize(() => {
          config.isLoading = false;
        }),
      );
    }
    // Returning the queue subject to anyone who's in dire need of this asset while being retrieved.
    // Once the asset has arrived, the response observable will pass the filter operator and will
    // then be switchMapped as the actual response.
    return config.queueSubject.pipe(
      filter((observable) => !!observable),
      first(),
      switchMap((res: Observable<ResponseFromServer<any>>) => res),
    );
  }

  setSharedAssets(assetKey: SharedStoreAssetKey, newRes: any): void {
    const config: SharedStoreAssetConfig<SharedStoreResponseType> = this.sharedAssetsConfigs[assetKey];
    config.response$ = of(newRes).pipe(shareReplay(1));
  }

  getSharedEnum(enumKey: string): Observable<{ [key: string]: string }> {
    return this.getSharedAsset('optionsLists').pipe(
      first(),
      switchMap((optionsList: OptionsLists) => of(optionsList[enumKey]))
    );
  }

  getTrafficSourcesByPublisherId(publisherId: number): Observable<ResponseFromServer<PublisherTrafficSource[]>> {
    return this.getSharedAsset('trafficSourcesByPublisherId', publisherId);
  }

  getPublisherForLayout(publisherId: number): Observable<PublisherEdit> {
    return this.getSharedAsset('publisherForLayout', publisherId);
  }

  resetSharedStore(): void {
    Object.values(this.sharedAssetsConfigs).forEach(config => {
      config.isLoading = false;
      config.response$ = null;
    });
  }

  resetSharedAsset(assetKey: SharedStoreAssetKey): void {
    const config: SharedStoreAssetConfig<SharedStoreResponseType> = this.sharedAssetsConfigs[assetKey];
    config.response$ = null;
    config.isLoading = false;
  }

}
